<template>
  <div class="gr-root">
    <ActivitiesFilterBar
      :initial-filter="filter"
      :is-filter-applied="isFilterApplied"
      @update:filter="handleFilterChange"
    />

    <section class="gr-section gr-activities">
      <div class="gr-container">
        <header class="gr-head">
          <p class="gr-eyebrow" aria-hidden="true">
            <span class="gr-eyebrow__index">{{
              isFinishedMode ? "Arsip" : "Daftar"
            }}</span>
            <span class="gr-eyebrow__rule" />
          </p>
          <h1 class="gr-h2 gr-activities__title">
            {{ pageHeading }}
          </h1>
          <p class="gr-lede">{{ pageIntro }}</p>
          <!-- Page-level total only: the list loads incrementally, so a
                   per-year count would be wrong the moment page two lands. -->
          <p class="gr-activities__count" aria-live="polite">
            {{ resultCountText }}
          </p>
        </header>

        <!-- First load only. Every later request keeps the real cards on
                 screen instead of replacing them with bones. -->
        <div v-if="showSkeletons" class="gr-activities__grid">
          <ActivityCardSkeleton v-for="i in PAGE_SIZE" :key="i" />
        </div>

        <template v-else>
          <template v-if="activityList.length">
            <template v-for="group in displayGroups" :key="group.year ?? 'all'">
              <h2 v-if="group.year !== null" class="gr-activities__year">
                {{ group.year }}
              </h2>
              <div class="gr-activities__grid">
                <div
                  v-for="activity in group.activities"
                  :key="activity.id"
                  :data-activity-id="activity.id"
                >
                  <ActivityListCard
                    :to="activity.to"
                    :content-img="activity.img"
                    :content-title="activity.title"
                    :content-date="activity.date"
                    :content-registration-end-date="
                      activity.registrationClosedDate
                    "
                    :content-city="activity.city"
                    :variant="
                      activity.status === 'ON_REGISTRATION'
                        ? 'default'
                        : 'finished'
                    "
                    :heading-level="group.year === null ? 2 : 3"
                    :content-status="activity.status"
                  />
                </div>
              </div>
            </template>
          </template>

          <!-- Failure sits below whatever is already on screen and never
                   replaces it. role="alert" announces it once; the polite
                   count region above does not repeat this text. -->
          <div v-if="activitiesError" class="gr-activities__alert" role="alert">
            <div class="gr-activities__alert-body">
              <p class="gr-activities__alert-title">Aktivitas gagal dimuat</p>
              <p class="gr-activities__alert-text">
                Koneksi ke server sedang bermasalah. Aktivitas yang sudah tampil
                tetap bisa dibuka.
              </p>
            </div>
            <button type="button" class="gr-btn gr-btn--outline" @click="retry">
              Coba lagi
            </button>
          </div>

          <!-- Rendered only when the request succeeded and returned
                   nothing: a failure is not an emptiness. -->
          <div v-else-if="showEmptyState" class="gr-activities__empty">
            <v-icon
              icon="mdi-calendar-alert"
              size="40"
              aria-hidden="true"
              class="gr-activities__empty-icon"
            />
            <template v-if="hasAnyFilter">
              <p class="gr-activities__empty-title">
                Tidak ada aktivitas yang cocok dengan filtermu
              </p>
              <p class="gr-activities__empty-text">
                Coba hapus filter untuk melihat lebih banyak aktivitas.
              </p>
              <button
                type="button"
                class="gr-btn gr-btn--outline mt-4"
                @click="clearAllFilters"
              >
                Hapus semua filter
              </button>
            </template>
            <template v-else>
              <p class="gr-activities__empty-title">
                Belum ada aktivitas di sini
              </p>
              <p class="gr-activities__empty-text">
                Lihat aktivitas yang pendaftarannya sedang dibuka.
              </p>
              <NuxtLink
                :to="{
                  path: LIST_ROUTE,
                  query: openRegistrationQuery,
                }"
                class="gr-btn gr-btn--outline mt-4"
              >
                Aktivitas yang sedang dibuka
              </NuxtLink>
            </template>
          </div>

          <!-- Page two is loaded on request, never by scroll position, so
                   the bottom of the page stays reachable. -->
          <div
            v-if="hasMoreActivities && activityList.length"
            class="gr-activities__more"
          >
            <button
              ref="loadMoreButton"
              type="button"
              class="gr-btn gr-btn--outline"
              :disabled="isLoading"
              @click="handleLoadMore"
            >
              {{ isLoading ? "Memuat…" : "Muat lebih banyak aktivitas" }}
            </button>
          </div>
        </template>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import ActivitiesFilterBar from "@/features/Activity/views/components/ActivitiesFilterBar.vue";
import ActivityListCard from "@/features/Activity/views/components/ActivityListCard.vue";
import ActivityCardSkeleton from "@/features/Activity/views/components/ActivityCardSkeleton.vue";
import {
  usePublicActivities,
  type PublicActivitiesFilter,
  type PublicActivityItem,
} from "@/features/Activity/composables/usePublicActivities";
import { groupActivitiesByYear } from "~/features/Activity/utils/groupActivitiesByYear";
import {
  ACTIVITY_STATUS_LABEL,
  type ActivityStatus,
} from "@/common/utils/activityStatus";

const LIST_ROUTE = "/activities";
const PAGE_SIZE = 8;

const route = useRoute();
const router = useRouter();

const SORT_VALUES = [
  "latest",
  "oldest",
  "nearest_reg",
  "oldest_reg",
  "finished_recent",
] as const;
type SortValue = (typeof SORT_VALUES)[number];

// Finished mode is grouped and ordered by `endDate`, so its default order must
// be `finished_recent`. Falling back to `latest` (creation order) would make
// page two arrive interleaved with the cards already on screen.
const parseSort = (value: unknown, status?: ActivityStatus): SortValue =>
  SORT_VALUES.includes(value as SortValue)
    ? (value as SortValue)
    : status === "FINISHED"
      ? "finished_recent"
      : "latest";

// Unknown or absent status means "all statuses": no status filtering.
const parseStatus = (value: unknown): ActivityStatus | undefined =>
  typeof value === "string" && Object.hasOwn(ACTIVITY_STATUS_LABEL, value)
    ? (value as ActivityStatus)
    : undefined;

const filter = reactive<PublicActivitiesFilter>({
  query: (route.query.query as string) || undefined,
  provinceId: Number(route.query.provinceId) || undefined,
  cityId: Number(route.query.cityId) || undefined,
  sort: parseSort(route.query.sort, parseStatus(route.query.status)),
  status: parseStatus(route.query.status),
});

const isFilterApplied = computed(() => !!filter.provinceId || !!filter.cityId);
const isFinishedMode = computed(() => filter.status === "FINISHED");

// Deliberately no page-level `cardStatus`. Each card reads the status the API
// returned for its own row: with no status filter active the list is mixed, and
// deriving the marker from the filter made every finished activity render the
// registration countdown instead, which reads "Pendaftaran ditutup" once the
// deadline has passed.

const pageHeading = computed(() =>
  isFinishedMode.value ? "Jejak Aktivitas" : "Aktivitas Relawan",
);

const pageIntro = computed(() =>
  isFinishedMode.value
    ? "Aktivitas yang sudah dijalankan, dikelompokkan menurut tahun selesainya."
    : "Cari aktivitas relawan yang sesuai dengan minat dan lokasimu.",
);

const {
  activities: activityList,
  isLoading,
  error: activitiesError,
  totalItems,
  hasMoreActivities,
  fetchActivities,
  loadMore,
} = usePublicActivities();

// One row of groups drives both modes. `year: null` means "no year heading",
// which is the non-finished mode: cards render directly under the h1.
const displayGroups = computed<
  { year: number | null; activities: PublicActivityItem[] }[]
>(() =>
  isFinishedMode.value
    ? groupActivitiesByYear(activityList.value)
    : [{ year: null, activities: activityList.value }],
);

const resultCountText = computed(() =>
  totalItems.value
    ? `Menampilkan ${activityList.value.length} dari ${totalItems.value} aktivitas.`
    : "Belum ada aktivitas yang ditampilkan.",
);

// useState, not ref: the first page settles on the server and only payload
// state reaches the client. A plain ref would rehydrate as false and let the
// empty state reappear after a server-rendered zero-result page.
const hasCompletedFirstLoad = useState<boolean>(
  "site:activities:first-load-done",
  () => false,
);

// Returns a truthy value on purpose: a handler resolving to undefined makes
// Nuxt treat the payload as absent and repeat the request on the client.
async function loadFirstPage(): Promise<{ loaded: boolean }> {
  await fetchActivities(1, PAGE_SIZE, filter);
  hasCompletedFirstLoad.value = true;
  return { loaded: true };
}

// Every filter value is in the key, so a filter change is a different key and
// Nuxt refetches instead of serving the previous payload.
const firstPageKey = computed(
  () =>
    `site:activities:${filter.status ?? "ALL"}:${filter.sort}:${filter.query ?? ""}:${filter.provinceId ?? ""}:${filter.cityId ?? ""}`,
);

// Fetched here rather than in onMounted so the first page also renders on the
// server. Not awaited: Nuxt already blocks the server render on pending async
// data, and a top-level await would move every composable below out of the
// setup context.
useAsyncData(firstPageKey.value, loadFirstPage, { watch: [firstPageKey] });

const showSkeletons = computed(
  () => isLoading.value && !hasCompletedFirstLoad.value,
);

// Emptiness is a successful answer of zero, never a failure and never a
// pending request.
const showEmptyState = computed(
  () =>
    hasCompletedFirstLoad.value &&
    !isLoading.value &&
    !activitiesError.value &&
    !activityList.value.length,
);

// Sort only reorders, so it never explains an empty result and is excluded.
const hasAnyFilter = computed(
  () =>
    !!filter.query || !!filter.provinceId || !!filter.cityId || !!filter.status,
);

const openRegistrationQuery = { status: "ON_REGISTRATION" as ActivityStatus };

function clearAllFilters(): void {
  router.replace({ query: {} });
}

// The composable only clears the error on a full refetch, so a retry of the
// next page has to clear it here or the alert would outlive its cause.
function retry(): void {
  activitiesError.value = null;
  if (activityList.value.length) loadMore(PAGE_SIZE, filter);
  else loadFirstPage();
}

const loadMoreButton = ref<HTMLButtonElement | null>(null);

// Spec 001 criterion 24 asks for focus to move to the first newly appended
// result. Done unconditionally that yanked the viewport 850px, because
// `focus()` scrolls its target into view and the new cards land below the
// fold. So the move is tied to how the button was actually operated:
//
//   keyboard  the visitor has no pointer to follow the new results with, so
//             focus moves and the browser scrolls to it, which is the point
//             of the criterion
//   pointer   the visitor can already see the new cards, nothing they were
//             reading has moved, and the polite region announces the new
//             count -- so the page stays exactly where they left it
async function handleLoadMore(): Promise<void> {
  const viaKeyboard = loadMoreButton.value?.matches(":focus-visible") ?? false;
  const firstNewIndex = activityList.value.length;
  await loadMore(PAGE_SIZE, filter);
  if (!viaKeyboard) return;

  const firstNew = activityList.value[firstNewIndex];
  if (!firstNew) return;
  await nextTick();
  document
    .querySelector<HTMLElement>(
      `[data-activity-id="${CSS.escape(firstNew.id)}"] a`,
    )
    ?.focus();
}

type RouteQuery = typeof route.query;

interface FilterState {
  query?: string;
  provinceId?: number;
  cityId?: number;
  sort?: SortValue;
  status?: ActivityStatus;
}

// The filter state itself is only ever written by the route watcher, so the URL
// stays the single source of truth and the single fetch trigger. A status
// change pushes history; anything else replaces it.
// Nuxt's default scroll behaviour returns to the top when the path changes, and
// a filter only rewrites the query string, so the visitor stayed wherever they
// had scrolled to while the list under them was replaced wholesale. Load more is
// the opposite case -- it appends, the visitor's position still means something,
// and handleLoadMore deliberately leaves the scroll alone.
function scrollToResultsTop(): void {
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  window.scrollTo({ top: 0, behavior: reduced ? "auto" : "smooth" });
}

function handleFilterChange(newFilter: FilterState): void {
  const status = parseStatus(newFilter.status);
  updateRouteQuery(newFilter, status !== filter.status);
  scrollToResultsTop();
}

function updateRouteQuery(newFilter: FilterState, pushHistory = false): void {
  const newQuery: Record<string, string | undefined> = {};

  if (newFilter.query) newQuery.query = newFilter.query;
  if (newFilter.provinceId) newQuery.provinceId = String(newFilter.provinceId);
  if (newFilter.cityId) newQuery.cityId = String(newFilter.cityId);
  const status = parseStatus(newFilter.status);
  newQuery.sort = parseSort(newFilter.sort, status);
  if (status) newQuery.status = status;

  if (pushHistory) router.push({ query: newQuery });
  else router.replace({ query: newQuery });
}

// Extracted so the watcher itself stays within the repo's complexity limit.
function hasQueryChanged(next: RouteQuery): boolean {
  return (
    next.query !== (filter.query || undefined) ||
    String(next.provinceId) !== String(filter.provinceId || undefined) ||
    String(next.cityId) !== String(filter.cityId || undefined) ||
    parseSort(next.sort, parseStatus(next.status)) !== filter.sort ||
    parseStatus(next.status) !== filter.status
  );
}

watch(
  () => route.query,
  (newQuery) => {
    if (!hasQueryChanged(newQuery)) return;

    filter.query = (newQuery.query as string) || undefined;
    filter.provinceId = Number(newQuery.provinceId) || undefined;
    filter.cityId = Number(newQuery.cityId) || undefined;
    filter.sort = parseSort(newQuery.sort, parseStatus(newQuery.status));
    filter.status = parseStatus(newQuery.status);

    // No fetch here: writing `filter` changes `firstPageKey`, and the
    // useAsyncData watcher on that key issues the refetch. Fetching here too
    // would fire two requests per filter change.
  },
  { deep: true },
);
</script>

<style scoped>
.gr-activities__title {
  font-size: clamp(1.8rem, 1.3rem + 2vw, 2.6rem);
}

.gr-activities__count {
  margin: 12px 0 0;
  color: var(--gr-ink-3);
  font-size: 0.875rem;
  line-height: 1.5;
}

.gr-activities__year {
  margin: 2.5rem 0 1rem;
  padding-top: 16px;
  border-top: 1px solid var(--gr-line);
  color: var(--gr-green);
  font-size: 1rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.1em;
  line-height: 1;
}

/* Two columns on a phone. The 150px track plus the 12px gap fits twice inside
   375px minus the container gutters, and collapses to one column on its own
   below roughly 330px, so an effective 188 CSS px still gets a usable card. */
.gr-activities__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 150px), 1fr));
  gap: 12px;
}

@media (min-width: 600px) {
  .gr-activities__grid {
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 260px), 1fr));
    gap: 20px;
  }
}

.gr-activities__alert {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 2rem;
  padding: 20px;
  border: 1px solid var(--gr-danger);
  border-radius: 4px;
  background-color: rgba(179, 38, 30, 0.06);
}

.gr-activities__alert-body {
  min-width: 0;
}

.gr-activities__alert-title {
  margin: 0 0 4px;
  color: var(--gr-danger);
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.4;
}

.gr-activities__alert-text {
  max-width: 60ch;
  margin: 0;
  color: var(--gr-ink-2);
  font-size: 0.9375rem;
  line-height: 1.6;
}

.gr-activities__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 24px;
  border: 1px dashed var(--gr-line-strong);
  border-radius: 4px;
  text-align: center;
}

.gr-activities__empty-icon {
  color: var(--gr-ink-3);
  opacity: 0.7;
}

.gr-activities__empty-title {
  margin: 16px 0 6px;
  color: var(--gr-ink);
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.4;
}

.gr-activities__empty-text {
  max-width: 46ch;
  margin: 0;
  color: var(--gr-ink-2);
  font-size: 0.9375rem;
  line-height: 1.6;
}

.gr-activities__more {
  display: flex;
  justify-content: center;
  margin-top: 2.5rem;
}

.mt-4 {
  margin-top: 1rem;
}
</style>
